import './stepsView.scss';
// @ vendors
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { recordEventCustomOnly } from 'utilities/uTagging';
// @ components
import { RAAccordionStep } from 'components/RAComponents/RAAccordionStep/RAAccordionStep';
import { RoadmapNavigation } from 'components/steps/roadmapNavigation';
import RAModal from 'components/RAComponents/commons/RAModal/RAModal';
// @ utilities
import { formatText } from 'core/i18n';
import UtagHelper from 'utilities/utagHelper';
// @ constants
import { PATH_GBI_TI, PATH_ROBO_TI } from 'constants/uTagConstants/paths';
import {
    SUITABILITY_TEST_INVESTMENT_ANSWERS,
    SUITABILITY_TEST_PROFILE_MODEL_ANSWERS,
} from 'constants/RAConstants/index';
import {
    EVENT_ACTION_MODAL_VIEW,
    EVENT_CATEGORY_SUITABILITY_TEST,
    EVENT_DETAIL_BUTTON_CLOSE,
    EVENT_DETAIL_BUTTON_NEXT,
    EVENT_DETAIL_BUTTON_PREV,
    EVENT_DETAIL_MODAL_OPEN_DEFAULT,
    EVENT_DETAIL_MODAL_STEP_4,
    EVENT_DETAIL_QUESTION_LOAD,
    EVENT_DETAIL_RECONSIDER_BUTTON,
    INVESTMENT_EXPERIENCE,
    INVESTMENT_EXPERIENCE_BUTTONS_ANSWERS,
    TEST_PROCESS_STEP_STEP_4,
} from 'constants/uTagConstants/RAConstants';
import ROBO from 'constants/userProfile';
import iconVerification from 'assets/icons/icono_verificacion_respuesta.svg';

export const RAInvestmentExperienceStep = ({
    userProfile,
    immSuitabilityTest,
    RATestWizard,
    raSuitabilityTest,
    raTestActions,
    formActions,
    modalCancelActions,
}) => {
    const [showModal, setShowModal] = useState(false);
    const accordionStep = immSuitabilityTest.get('accordionStep');

    const getCurrentStepAndSubStep = () => {
        const { currentStep } = RATestWizard;
        const { currentSubStep } = RATestWizard;

        return {
            currentStep,
            currentSubStep,
        };
    };

    const hasSelectedOption = () => {
        return SUITABILITY_TEST_INVESTMENT_ANSWERS.some(questionId => {
            const answer = userProfile?.RASuitabilityTest?.answers?.[questionId];

            return answer && answer !== 'answer1';
        });
    };

    useEffect(() => {
        window.scrollTo(0, 0);
        const { currentStep, currentSubStep } = getCurrentStepAndSubStep();
        const finalized = RATestWizard.steps?.[currentStep].steps?.[currentSubStep].finished;
        !finalized && !hasSelectedOption() && raSuitabilityTest.fillAccordionDefaultAnswers();
        const pathForTag = userProfile.investmentType === ROBO ? PATH_ROBO_TI : PATH_GBI_TI;
        UtagHelper.recordEventRoboGBI(
            pathForTag,
            EVENT_CATEGORY_SUITABILITY_TEST,
            TEST_PROCESS_STEP_STEP_4,
            EVENT_DETAIL_QUESTION_LOAD,
            userProfile
        );
    }, []);

    useEffect(() => {
        const expandedRow = immSuitabilityTest.getIn(['accordionStep', 'expandedRowId']);
        if (expandedRow) {
            const pathForTag = userProfile.investmentType === ROBO ? PATH_ROBO_TI : PATH_GBI_TI;
            UtagHelper.recordEventRoboGBI(
                pathForTag,
                EVENT_CATEGORY_SUITABILITY_TEST,
                TEST_PROCESS_STEP_STEP_4,
                `${INVESTMENT_EXPERIENCE[expandedRow]} abrir`,
                userProfile
            );
        }
    }, [immSuitabilityTest.getIn(['accordionStep', 'expandedRowId'])]);

    const onHandleCancelButton = () => {
        modalCancelActions.showModalCancel();

        recordEventCustomOnly({
            accionEvento: EVENT_ACTION_MODAL_VIEW,
            categoriaEvento: EVENT_CATEGORY_SUITABILITY_TEST,
            etiquetaEvento: TEST_PROCESS_STEP_STEP_4,
        });
    };

    const onHandleContinueButton = () => {
        const { currentStep, currentSubStep } = getCurrentStepAndSubStep();
        const { lastSubStep } = RATestWizard;
        const finalized = RATestWizard.steps?.[currentStep].steps?.[currentSubStep].finished;
        const data = {
            step: currentStep,
            substep: currentSubStep,
        };

        const pathForTag = userProfile.investmentType === ROBO ? PATH_ROBO_TI : PATH_GBI_TI;
        if (immSuitabilityTest.getIn(['coherenceError', 'allowToPass']) && hasSelectedOption()) {
            if (finalized) {
                raSuitabilityTest.resetCoherenceError();
                raTestActions.nextStep(data);
            } else if (lastSubStep) {
                raTestActions.finishStep(data);
            } else {
                raTestActions.finishSubStep(data);
            }
            UtagHelper.recordEventRoboGBI(
                pathForTag,
                EVENT_CATEGORY_SUITABILITY_TEST,
                TEST_PROCESS_STEP_STEP_4,
                EVENT_DETAIL_BUTTON_NEXT,
                userProfile
            );
        } else {
            UtagHelper.recordEventRoboGBI(
                pathForTag,
                EVENT_CATEGORY_SUITABILITY_TEST,
                TEST_PROCESS_STEP_STEP_4,
                `${EVENT_DETAIL_MODAL_STEP_4} ${EVENT_DETAIL_MODAL_OPEN_DEFAULT}`,
                userProfile
            );
            setShowModal(true);
        }

        const continueEventTag = () => {
            const immAnswers = immSuitabilityTest.get('answers');
            const accordionStep = immSuitabilityTest.get('accordionStep');
            const rows = accordionStep.get('rows').toJS();
            const zeroPad = (num, places) => String(num).padStart(places, '0');

            const answers = rows.map((row, index) => {
                const answer = immAnswers.get(row.id).replace(/\D/g, '');

                return `answer${zeroPad(index + 1, 2)}_${answer - 1} | `;
            });

            return `siguiente_${answers}`.slice(0, -3).replace(/,/g, '');
        };

        recordEventCustomOnly({
            accionEvento: TEST_PROCESS_STEP_STEP_4,
            categoriaEvento: EVENT_CATEGORY_SUITABILITY_TEST,
            etiquetaEvento: continueEventTag(),
        });
    };

    const onHandlePrevButton = () => {
        raSuitabilityTest.clearCoherenceError();
        raTestActions.prevStep(getCurrentStepAndSubStep());
        const pathForTag = userProfile.investmentType === ROBO ? PATH_ROBO_TI : PATH_GBI_TI;
        UtagHelper.recordEventRoboGBI(
            pathForTag,
            EVENT_CATEGORY_SUITABILITY_TEST,
            TEST_PROCESS_STEP_STEP_4,
            EVENT_DETAIL_BUTTON_PREV,
            userProfile
        );
    };

    const onSelectAccordionButton = (rowId, buttonId) => {
        const product = INVESTMENT_EXPERIENCE[rowId];
        const answer = INVESTMENT_EXPERIENCE_BUTTONS_ANSWERS[buttonId];

        const pathForTag = userProfile.investmentType === ROBO ? PATH_ROBO_TI : PATH_GBI_TI;
        UtagHelper.recordEventRoboGBI(
            pathForTag,
            EVENT_CATEGORY_SUITABILITY_TEST,
            TEST_PROCESS_STEP_STEP_4,
            `${product} - ${answer}`,
            userProfile
        );

        raSuitabilityTest.selectAccordionButton(rowId, buttonId);
    };

    const recordEvent = (eventDetail = '', eventCategory = EVENT_CATEGORY_SUITABILITY_TEST) => {
        const pathForTag = userProfile.investmentType === ROBO ? PATH_ROBO_TI : PATH_GBI_TI;

        UtagHelper.recordEventRoboGBI(pathForTag, eventCategory, TEST_PROCESS_STEP_STEP_4, eventDetail, userProfile);
    };

    const toggleModal = () => {
        setShowModal(prevState => !prevState);
    };

    const handleOnClickTooltip = (eventDetail = '') => {
        const pathForTag = userProfile.investmentType === ROBO ? PATH_ROBO_TI : PATH_GBI_TI;
        UtagHelper.recordEventRoboGBI(
            pathForTag,
            EVENT_CATEGORY_SUITABILITY_TEST,
            TEST_PROCESS_STEP_STEP_4,
            eventDetail,
            userProfile
        );
    };

    const getModal = () => {
        const message = immSuitabilityTest.getIn(['coherenceError', 'message']);
        let modal;
        const { currentStep, currentSubstep } = getCurrentStepAndSubStep();
        const data = {
            step: currentStep,
            substep: currentSubstep,
        };

        const pathForTag = userProfile.investmentType === ROBO ? PATH_ROBO_TI : PATH_GBI_TI;

        if (!hasSelectedOption()) {
            const titleModal = formatText('raModal-productsExperienceModalTitle');
            const textModal = formatText('raModal-productsExperienceModal');
            const firstButtonModal = formatText('raModal-continueButton');
            // Pass with warnings
            const firstActionModal = () => {
                UtagHelper.recordEventRoboGBI(
                    pathForTag,
                    EVENT_CATEGORY_SUITABILITY_TEST,
                    TEST_PROCESS_STEP_STEP_4,
                    `${EVENT_DETAIL_MODAL_STEP_4} ${EVENT_DETAIL_BUTTON_NEXT}`,
                    userProfile
                );

                raTestActions.nextStep({
                    step: 0,
                    substep: 3,
                });
                raTestActions.finishStep(data);
            };
            const secondButtonModal = formatText('raModal-reconsiderButton');
            const secondActionModal = () => {
                UtagHelper.recordEventRoboGBI(
                    pathForTag,
                    EVENT_CATEGORY_SUITABILITY_TEST,
                    TEST_PROCESS_STEP_STEP_4,
                    `${EVENT_DETAIL_MODAL_STEP_4} ${EVENT_DETAIL_RECONSIDER_BUTTON}`,
                    userProfile
                );
                toggleModal();
            };

            const hideModal = () => {
                UtagHelper.recordEventRoboGBI(
                    pathForTag,
                    EVENT_CATEGORY_SUITABILITY_TEST,
                    TEST_PROCESS_STEP_STEP_4,
                    `${EVENT_DETAIL_MODAL_STEP_4} ${EVENT_DETAIL_BUTTON_CLOSE}`,
                    userProfile
                );
                toggleModal();
            };

            modal = (
                <RAModal
                    show={showModal}
                    title={titleModal}
                    text={textModal}
                    hide={hideModal}
                    firstButton={firstButtonModal}
                    firstAction={firstActionModal}
                    secondButton={secondButtonModal}
                    secondAction={secondActionModal}
                />
            );
        } else if (message) {
            const logoModal = iconVerification;
            const titleModal = formatText('raModal-title');
            const textModal = message;
            const firstButtonModal = formatText('raModal-verifyButton');

            modal = (
                <RAModal
                    show={showModal}
                    logo={logoModal}
                    title={titleModal}
                    text={textModal}
                    firstButton={firstButtonModal}
                />
            );
        }

        return modal;
    };

    return (
        <div>
            <div className="steps-view">
                <RAAccordionStep
                    expandAccordionRow={raSuitabilityTest.expandAccordionRow}
                    expandedRowId={accordionStep.get('expandedRowId')}
                    formActions={formActions}
                    immAnswers={immSuitabilityTest.get('answers')}
                    profileModel={SUITABILITY_TEST_PROFILE_MODEL_ANSWERS}
                    rows={accordionStep.get('rows').toJS()}
                    selectAccordionButton={onSelectAccordionButton}
                    subtitle={accordionStep.get('subtitle')}
                    title={accordionStep.get('title')}
                    userProfile={userProfile}
                    validateStep={raSuitabilityTest.validateStep}
                    onClickTooltip={handleOnClickTooltip}
                    recordEvent={recordEvent}
                />
                {getModal()}
            </div>
            <hr />
            <div className="steps-view__footer">
                <RoadmapNavigation
                    buttonContinueType="submit"
                    buttonContinueAction={onHandleContinueButton}
                    buttonContinueText={formatText('suitabilityTest-next')}
                    buttonPrevText={formatText('suitabilityTest-prev')}
                    buttonCancelAction={onHandleCancelButton}
                    buttonCancelText={formatText('suitabilityTest-getOut')}
                    buttonPrevAction={onHandlePrevButton}
                    buttonContinueDisabled={false}
                />
            </div>
        </div>
    );
};

RAInvestmentExperienceStep.propTypes = {
    RATestWizard: PropTypes.object,
    immSuitabilityTest: PropTypes.object,
    raSuitabilityTest: PropTypes.object,
    raTestActions: PropTypes.object,
    modalCancelActions: PropTypes.object,
    formActions: PropTypes.object,
    userProfile: PropTypes.object,
};
